import type { CalendarClient, CalendarEvent } from '@wtfalch/calendar/calendar';
import { Button, Icon } from '@wtfalch/design';
import { declineCounter } from '@wtfalch/email/imip';
import type { MailClient, MailIdentity, Message } from '@wtfalch/email/mailbox';
import { replyDraft, send as sendMail } from '@wtfalch/email/mailbox';
import { type ReactNode, useEffect, useState } from 'react';

import { eventTimeLabel } from '../calendar/layout.ts';
import {
  type CounterImip,
  durationInMinutes,
  eventDuration,
  loadOrganizedCounter,
  minutesBetween,
  toLocalDateTime,
} from './counter.ts';
import { displayName } from './format.ts';

type Resolution = 'loading' | 'none' | { imip: CounterImip; event: CalendarEvent };

/**
 * A message's `.ics` attachment, upgraded to the proposal card when it is a
 * `COUNTER` against an event this account organises (agora#284) --
 * `fallback` (today's bare chip) for everything else: still loading, no
 * calendar part, a `DECLINECOUNTER`, or a `COUNTER` for an event this
 * account does not organise.
 */
export function CounterAttachment({
  client,
  calendarClient,
  identity,
  message,
  fallback,
}: {
  client: MailClient;
  calendarClient: CalendarClient;
  identity: MailIdentity | null;
  message: Message;
  fallback: ReactNode;
}) {
  const [resolution, setResolution] = useState<Resolution>('loading');
  const attachmentKey = message.attachments.map((a) => a.blobId).join(',');

  // Keyed on the message and its attachment, not the object: a star, a reply
  // or a cancelled send refetches the thread and hands this a new `message`
  // for the same mail, and that must not reset an answered card to loading.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see above
  useEffect(() => {
    let live = true;
    setResolution('loading');
    loadOrganizedCounter(client, calendarClient, message)
      .then((result) => {
        if (live) setResolution(result ?? 'none');
      })
      .catch(() => {
        if (live) setResolution('none');
      });
    return () => {
      live = false;
    };
  }, [client, calendarClient, message.id, attachmentKey]);

  if (resolution === 'loading' || resolution === 'none') return <>{fallback}</>;
  return (
    <CounterCard
      client={client}
      calendarClient={calendarClient}
      identity={identity}
      message={message}
      imip={resolution.imip}
      event={resolution.event}
    />
  );
}

/**
 * "<proposer> proposed <new time>" beside "You set <current time>", the
 * optional comment, and Accept/Decline (agora#284).
 *
 * Accept moves the organiser's own event to the proposed slot through the
 * calendar client, `sendSchedulingMessages: true` so every attendee's copy
 * updates too -- the same call `calendar/Event.tsx`'s own RSVP makes.
 * Decline never touches the event; it only sends the proposer a
 * `DECLINECOUNTER` (RFC 5546 §3.2.8) as ordinary mail, threaded onto the
 * counter the way any reply would be.
 */
export function CounterCard({
  client,
  calendarClient,
  identity,
  message,
  imip,
  event,
}: {
  client: MailClient;
  calendarClient: CalendarClient;
  identity: MailIdentity | null;
  message: Message;
  imip: CounterImip;
  event: CalendarEvent;
}) {
  const [outcome, setOutcome] = useState<'accepted' | 'declined' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentStart = new Date(event.start);
  const currentEnd = new Date(currentStart.getTime() + durationInMinutes(event.duration) * 60_000);

  const accept = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      // `event.timeZone` is written back exactly as it was -- `null` stays
      // `null` (a floating, all-day event never gains a zone it didn't
      // have) -- and the duration keeps a whole-day shape for an all-day
      // event rather than always writing hours (`eventDuration`).
      await calendarClient.updateEvent(
        event.id,
        {
          start: toLocalDateTime(imip.start, event.timeZone),
          timeZone: event.timeZone,
          duration: eventDuration(minutesBetween(imip.start, imip.end), event.isAllDay),
        },
        { sendSchedulingMessages: true },
      );
      setOutcome('accepted');
    } catch {
      setError('Could not update the event. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const decline = async () => {
    if (busy || !identity) return;
    setBusy(true);
    setError(null);
    try {
      const part = declineCounter({
        uid: imip.uid,
        sequence: imip.sequence,
        recurrenceId: imip.recurrenceId,
        organizer: imip.organizer,
        attendee: imip.attendee,
      });
      const draft = replyDraft(message, { identity, quote: false });
      await sendMail(client, {
        ...draft,
        // The verified From address only: `replyDraft` would answer to
        // `Reply-To`, which a sender who passes the From check can point at
        // an address of their own.
        to: message.from.slice(0, 1),
        text: "That time doesn't work — declining.",
        calendar: part,
      });
      setOutcome('declined');
    } catch {
      setError('Could not send the decline. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (outcome) {
    return (
      <div className="card flex flex-col gap-2 w-full py-3 px-4">
        <p className="text-sm">
          {outcome === 'accepted'
            ? 'Accepted — the event was updated.'
            : `Declined — ${displayName(imip.attendee)} was notified.`}
        </p>
      </div>
    );
  }

  const [proposedDay = '', proposedRange = ''] = eventTimeLabel(imip.start, imip.end).split(' · ');
  const [currentDay = '', currentRange = ''] = eventTimeLabel(currentStart, currentEnd).split(
    ' · ',
  );
  const proposer = displayName(imip.attendee);

  return (
    <div className="card w-full mail-counter">
      <div className="flex items-center gap-3 mail-counter-head">
        <span className="flex text-info">
          <Icon name="calendar" size={18} />
        </span>
        <span className="flex flex-col grow min-w-0 gap-0.5">
          <span className="text-sm font-strong">{proposer.split(' ')[0]} proposed a new time</span>
          <span className="text-xs text-muted">
            {event.title}. You organise this one, so it is yours to take or leave.
          </span>
          {!message.senderAuthenticated && (
            <span className="text-xs text-bad" role="status">
              Unverified sender: we could not verify the sender's domain for this mail, so it may
              not really be from {proposer}. It may be forged or forwarded. Check before you accept.
            </span>
          )}
        </span>
      </div>
      <div className="flex gap-0 mail-counter-times">
        <span className="flex flex-col grow gap-1">
          <span className="mail-counter-label">You set</span>
          <span className="text-sm font-strong mail-counter-old">{currentDay}</span>
          <span className="text-sm text-muted">{currentRange}</span>
        </span>
        <span className="flex items-center text-muted px-5">
          <Icon name="arrow-right" size={18} />
        </span>
        <span className="flex flex-col grow gap-1">
          <span className="mail-counter-label">{proposer} proposes</span>
          <span className="h3">{proposedDay}</span>
          <span className="text-sm text-muted">{proposedRange}</span>
        </span>
      </div>
      {imip.comment && <p className="text-sm italic m-0 mail-counter-note">“{imip.comment}”</p>}
      <p className="text-xs text-muted m-0 mail-counter-note">
        Accepting moves the event and tells everyone else invited. Declining leaves it where it is
        and says so, once.
      </p>
      {error && <p className="text-sm text-bad m-0 mail-counter-note">{error}</p>}
      <div className="flex items-center gap-2 mail-counter-foot">
        <div className="grow" />
        <Button
          kind="default"
          size="sm"
          busy={busy}
          disabled={!identity}
          onPress={() => void decline()}
        >
          Decline
        </Button>
        <Button kind="primary" size="sm" busy={busy} onPress={() => void accept()}>
          Accept the new time
        </Button>
      </div>
    </div>
  );
}
